import { useState, useRef, useEffect } from "react";
import { Eye, Download, Loader2, FileText } from "lucide-react";

/**
 * View + Download controls with hover thumbnail preview for lead-uploaded files.
 * Fetches via the authenticated api client (GET /lead-mgmt/{leadId}/files/{path})
 * and opens/downloads via a blob URL so the browser bypasses cookie-less auth.
 */
export default function LeadFileActions({ file, api, leadId, testid }) {
  const cache = useRef(null);
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hover, setHover] = useState(false);

  useEffect(() => () => { if (cache.current) URL.revokeObjectURL(cache.current); }, []);

  if (!file?.storage_path) return null;
  const name = file.name || "document";
  const mime = file.mime || "";
  const isImg = /^image\//i.test(mime) || /\.(png|jpe?g|webp|gif)$/i.test(name);
  const isPdf = mime === "application/pdf" || /\.pdf$/i.test(name);

  const ensureBlob = async () => {
    if (cache.current) return cache.current;
    setLoading(true);
    try {
      const res = await api.get(`/lead-mgmt/${leadId}/files/${file.storage_path}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      cache.current = url;
      setBlobUrl(url);
      return url;
    } catch (e) {
      return null;
    } finally { setLoading(false); }
  };

  const stop = (e) => { e.preventDefault(); e.stopPropagation(); };
  const onView = async (e) => {
    stop(e);
    const url = await ensureBlob();
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };
  const onDownload = async (e) => {
    stop(e);
    const url = await ensureBlob();
    if (!url) return;
    const a = document.createElement("a");
    a.href = url; a.download = name;
    document.body.appendChild(a); a.click(); a.remove();
  };
  const onEnter = () => { setHover(true); if (isImg && !cache.current) ensureBlob(); };

  return (
    <span className="relative inline-flex items-center gap-0.5"
          onMouseEnter={onEnter} onMouseLeave={() => setHover(false)}>
      <button type="button" onMouseDown={stop} onClick={onView}
              className="p-1 rounded hover:bg-teal-50 text-gray-600 hover:text-teal-700 transition-colors"
              data-testid={`${testid}-view`} title={`View ${name}`}>
        <Eye size={12}/>
      </button>
      <button type="button" onMouseDown={stop} onClick={onDownload}
              className="p-1 rounded hover:bg-teal-50 text-gray-600 hover:text-teal-700 transition-colors"
              data-testid={`${testid}-download`} title={`Download ${name}`}>
        <Download size={12}/>
      </button>
      {hover && (
        <div className="absolute z-50 bottom-full left-0 mb-2 rounded-md border border-gray-200 bg-white shadow-lg p-2 pointer-events-none">
          {loading ? (
            <div className="flex items-center justify-center h-24 w-40">
              <Loader2 className="animate-spin text-teal-600" size={20}/>
            </div>
          ) : isImg && (blobUrl || cache.current) ? (
            <img src={blobUrl || cache.current} alt={name}
                 className="max-h-40 max-w-[14rem] object-contain rounded"/>
          ) : (
            <div className="flex flex-col items-center gap-1 p-3 w-40">
              <FileText size={28} className={isPdf ? "text-rose-500" : "text-gray-400"}/>
              <div className="text-[11px] text-gray-700 truncate max-w-full">{name}</div>
              <div className="text-[10px] text-gray-400">
                {isPdf ? "PDF" : (mime || "File")} · {Math.round((file.size || 0) / 1024)} KB
              </div>
            </div>
          )}
        </div>
      )}
    </span>
  );
}
